import React, { useEffect, useState } from 'react';
import { accountAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const RegisterModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [accountId, setAccountId] = useState('');
  const [thresholdEntry, setThresholdEntry] = useState('-5.00');
  const [thresholdMin, setThresholdMin] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!accountId.trim()) { setError('계좌번호를 입력해주세요.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/v1/accounts', {
        accountId: accountId.trim(),
        isBBook: true,
        isActive: true,
        thresholdEntry: parseFloat(thresholdEntry),
        thresholdMin: parseFloat(thresholdMin),
        thresholdMax: -15.00,
        symbolMode: 'ALL',
      });
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.message || '등록 실패. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>B북 계좌 등록</h2>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={modalStyles.error}>{error}</div>}

        <div style={modalStyles.body}>
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>계좌번호 <span style={{ color: '#ff6b6b' }}>*</span></label>
            <input
              style={modalStyles.input}
              placeholder="예: 102716"
              value={accountId}
              onChange={e => setAccountId(e.target.value)}
              autoFocus
            />
          </div>

          <div style={modalStyles.row}>
            <div style={modalStyles.field}>
              <label style={modalStyles.label}>진입 임계값 (%)</label>
              <input
                style={modalStyles.input}
                type="number"
                step="0.01"
                value={thresholdEntry}
                onChange={e => setThresholdEntry(e.target.value)}
              />
              <span style={modalStyles.hint}>이 값 이하로 떨어지면 A북 전환</span>
            </div>
            <div style={modalStyles.field}>
              <label style={modalStyles.label}>복귀 임계값 (%)</label>
              <input
                style={modalStyles.input}
                type="number"
                step="0.01"
                value={thresholdMin}
                onChange={e => setThresholdMin(e.target.value)}
              />
              <span style={modalStyles.hint}>0이면 비활성 (포지션 청산 시 복귀)</span>
            </div>
          </div>
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.cancelBtn} onClick={onClose}>취소</button>
          <button
            style={loading ? { ...modalStyles.submitBtn, opacity: 0.6 } : modalStyles.submitBtn}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'B_BOOK' | 'A_BOOK'>('ALL');
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const fetchAccounts = () => {
    setLoading(true);
    accountAPI.getAll().then(res => {
      setAccounts(res.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAccounts(); }, []);

  const filtered = accounts.filter(acc => {
    const matchSearch = acc.accountId.includes(search) || (acc.mt5Group || '').includes(search);
    const matchFilter = filter === 'ALL' || (filter === 'B_BOOK' && acc.isBBook) || (filter === 'A_BOOK' && !acc.isBBook);
    return matchSearch && matchFilter;
  });

  return (
    <div style={styles.container}>
      {showModal && (
        <RegisterModal onClose={() => setShowModal(false)} onSuccess={fetchAccounts} />
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>계좌 관리</h1>
          <p style={styles.subtitle}>총 {accounts.length}개 계좌</p>
        </div>
        <button style={styles.registerBtn} onClick={() => setShowModal(true)}>
          + B북 계좌 등록
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="🔍 계좌번호 또는 그룹 검색"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={styles.filterGroup}>
          {(['ALL', 'B_BOOK', 'A_BOOK'] as const).map(f => (
            <button
              key={f}
              style={filter === f ? { ...styles.filterBtn, ...styles.filterBtnActive } : styles.filterBtn}
              onClick={() => setFilter(f)}
            >
              {f === 'ALL' ? '전체' : f === 'B_BOOK' ? '🟢 B북' : '🔴 A북'}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.tableWrapper}>
        {loading ? (
          <div style={styles.loading}>로딩 중...</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>계좌번호</th>
                <th style={styles.th}>구분</th>
                <th style={styles.th}>등급</th>
                <th style={styles.th}>진입 임계값</th>
                <th style={styles.th}>복귀 임계값</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>상세</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((acc: any) => (
                <tr key={acc.id} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={styles.accountId}>{acc.accountId}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={acc.isBBook ? styles.badgeB : styles.badgeA}>
                      {acc.isBBook ? 'B북' : 'A북'}
                    </span>
                  </td>
                  <td style={styles.td}><GradeBadge grade={acc.grade} /></td>
                  <td style={styles.td}>{acc.thresholdEntry}%</td>
                  <td style={styles.td}>
                    {acc.thresholdMin === 0 || acc.thresholdMin === '0.00'
                      ? <span style={{ color: 'rgba(255,255,255,0.3)' }}>비활성</span>
                      : `${acc.thresholdMin}%`}
                  </td>
                  <td style={styles.td}>
                    <span style={acc.active ? styles.statusActive : styles.statusInactive}>
                      {acc.active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button style={styles.detailBtn} onClick={() => navigate(`/accounts/${acc.accountId}`)}>
                      상세 →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filtered.length === 0 && (
          <div style={styles.empty}>조건에 맞는 계좌가 없습니다.</div>
        )}
      </div>
    </div>
  );
};

const GradeBadge: React.FC<{ grade: string }> = ({ grade }) => {
  const colors: Record<string, string> = { S: '#f7971e', A: '#667eea', B: '#00d2ff', C: '#aaa' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: `${colors[grade] || '#aaa'}20`, color: colors[grade] || '#aaa' }}>
      {grade}
    </span>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  registerBtn: {
    padding: '10px 20px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
    border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px',
    fontWeight: 600, cursor: 'pointer',
  },
  toolbar: { display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' },
  search: {
    flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '10px 16px', color: '#fff', fontSize: '14px', outline: 'none',
  },
  filterGroup: { display: 'flex', gap: '8px' },
  filterBtn: {
    padding: '8px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer',
  },
  filterBtnActive: { background: 'rgba(102,126,234,0.2)', borderColor: '#667eea', color: '#667eea' },
  tableWrapper: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.05)' },
  td: { padding: '12px 16px', color: 'rgba(255,255,255,0.8)', fontSize: '14px' },
  accountId: { fontFamily: 'monospace', background: 'rgba(102,126,234,0.15)', padding: '2px 8px', borderRadius: '4px', color: '#667eea' },
  badgeB: { padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: 'rgba(0,210,255,0.15)', color: '#00d2ff' },
  badgeA: { padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: 'rgba(255,107,107,0.15)', color: '#ff6b6b' },
  statusActive: { color: '#52c41a', fontSize: '12px' },
  statusInactive: { color: '#aaa', fontSize: '12px' },
  detailBtn: { background: 'none', border: 'none', color: '#667eea', fontSize: '13px', cursor: 'pointer' },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)',
  },
  modal: {
    background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '20px', width: '480px', maxWidth: '95vw',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  title: { color: '#fff', fontSize: '20px', fontWeight: 700, margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' },
  error: { margin: '16px 28px 0', padding: '12px 16px', background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '10px', color: '#ff6b6b', fontSize: '13px' },
  body: { padding: '24px 28px' },
  field: { marginBottom: '16px', flex: 1 },
  label: { display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '8px', fontWeight: 500 },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '10px 14px', color: '#fff', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box',
  },
  hint: { display: 'block', color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginTop: '4px' },
  row: { display: 'flex', gap: '16px' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    padding: '20px 28px 24px', borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  cancelBtn: {
    padding: '10px 20px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 24px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
    border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px',
    fontWeight: 600, cursor: 'pointer',
  },
};

export default AccountsPage;
